import {
  type ActionReducerMapBuilder,
  type AsyncThunk,
  type PayloadAction,
  createSlice
} from "@reduxjs/toolkit"

import { logout } from "../../LoginPage/redux/auth.thunks.ts"
import { CONTRACT_STATUSES } from "../constants/contract-statuses-keys.ts"
import type { Contract, ContractListItem } from "../types/types.ts"
import { contractsSliceName } from "./contracts.constants.ts"
import {
  approveContract,
  cancelContract,
  createContract,
  duplicateContract,
  emailContractToClient,
  fetchContract,
  fetchContractInitialData,
  fetchContracts,
  generateContract,
  getContractFilterOptions,
  getContractPreview,
  rejectContract,
  saveContract,
  sendContractToApproval,
  submitContractForReview,
  updateContract
} from "./contracts.thunks.ts"
import { type ContractsState } from "./contracts.types.ts"

type BooleanKeys<T> = {
  [K in keyof T]: T[K] extends boolean ? K : never
}[keyof T]

const addFetchingCases = <T>(
  builder: ActionReducerMapBuilder<ContractsState>,
  thunk: AsyncThunk<T, any, any>,
  key: BooleanKeys<ContractsState> = "saveFetching"
) => {
  builder
    .addCase(thunk.pending, (state) => {
      state[key] = true
    })
    .addCase(thunk.fulfilled, (state) => {
      state[key] = false
    })
    .addCase(thunk.rejected, (state) => {
      state[key] = false
    })
}

// Initial State
const initialState: ContractsState = {
  result: {
    count: 0,
    results: []
  },

  contract: null,

  filters: {
    q: null,
    status: CONTRACT_STATUSES.PENDING_TO_REVIEW
  },

  filterOptions: {
    status: []
  },

  fetching: false,
  fetchingList: false,
  saveFetching: false
}

// Slice
const contractsSlice = createSlice({
  name: contractsSliceName,
  initialState,
  reducers: {
    contractsSetFilters: (
      state,
      action: PayloadAction<Partial<ContractsState["filters"]>>
    ) => {
      state.filters = { ...state.filters, ...action.payload }
    }
  },
  extraReducers: (builder) => {
    addFetchingCases(builder, getContractPreview)
    addFetchingCases(builder, createContract)
    addFetchingCases(builder, generateContract)
    addFetchingCases(builder, updateContract)
    addFetchingCases(builder, saveContract)
    addFetchingCases(builder, sendContractToApproval)
    addFetchingCases(builder, submitContractForReview)
    addFetchingCases(builder, approveContract)
    addFetchingCases(builder, cancelContract)
    addFetchingCases(builder, duplicateContract)
    addFetchingCases(builder, rejectContract)
    addFetchingCases(builder, fetchContractInitialData, "fetching")

    builder
      // fetchContracts
      .addCase(fetchContracts.pending, (state) => {
        state.fetchingList = true
      })
      .addCase(fetchContracts.fulfilled, (state, action) => {
        const data = action?.payload || []

        state.fetchingList = false
        state.result = { count: data?.length, results: data }
      })
      .addCase(fetchContracts.rejected, (state) => {
        state.fetchingList = false
      })

      // fetchContract
      .addCase(fetchContract.pending, (state) => {
        state.fetching = true
      })
      .addCase(fetchContract.fulfilled, (state, action) => {
        state.fetching = false
        state.contract = action?.payload
      })
      .addCase(fetchContract.rejected, (state) => {
        state.fetching = false
      })

      // emailContractToClient
      .addCase(emailContractToClient.pending, (state) => {
        state.saveFetching = true
      })
      .addCase(emailContractToClient.fulfilled, (state, action) => {
        state.saveFetching = false
        if (state.contract?.id === action.payload.id) {
          state.contract = { ...state.contract, ...action.payload } as Contract
        }
        state.result.results = state.result.results.map((contract) =>
          contract.id === action.payload.id
            ? ({ ...contract, ...action.payload } as ContractListItem)
            : contract
        )
      })
      .addCase(emailContractToClient.rejected, (state) => {
        state.saveFetching = false
      })

      // fetchFilterOptions
      .addCase(getContractFilterOptions.pending, (state) => {
        state.fetching = true
      })
      .addCase(getContractFilterOptions.fulfilled, (state, action) => {
        state.fetching = false
        state.filterOptions = action?.payload
      })
      .addCase(getContractFilterOptions.rejected, (state) => {
        state.fetching = false
      })

      .addCase(logout.fulfilled, () => {
        console.log("contract logout")
        return initialState
      })
  }
})
export const { contractsSetFilters } = contractsSlice.actions
export default contractsSlice
